import React from "react";
import PropTypes from "prop-types";
import { FormControl, MenuItem, Select } from "@mui/material";
import { ChevronDown } from "lucide-react";

import Pagination from "../../Pagination";
import {
  UI_TABLE_DEFAULT_ROWS_PER_PAGE_OPTIONS,
  withSelectableCurrentPageSize,
} from "../../../utils/tablePageSize";
import { clx } from "../../../utils/Helpers";

import "./_UIPaginationBar.scss";

/** @param {string} template e.g. `Showing {from}–{to} of {total} items` */
function renderRangeTemplate(template, from, to, total, highlightClassName) {
  const nodes = [];
  let key = 0;
  let i = 0;

  while (i < template.length) {
    if (template.startsWith("{from}", i)) {
      const toPos = template.indexOf("{to}", i + "{from}".length);
      if (toPos === -1) {
        nodes.push(template.slice(i));
        break;
      }
      const sep = template.slice(i + "{from}".length, toPos);
      nodes.push(
        <span key={`range-ft-${key++}`} className={highlightClassName}>
          {from}
          {sep}
          {to}
        </span>,
      );
      i = toPos + "{to}".length;
      continue;
    }
    if (template.startsWith("{total}", i)) {
      nodes.push(String(total));
      i += "{total}".length;
      continue;
    }

    const nextFrom = template.indexOf("{from}", i);
    const nextTotal = template.indexOf("{total}", i);
    const candidates = [nextFrom, nextTotal].filter((x) => x >= 0);
    const next = candidates.length ? Math.min(...candidates) : -1;
    if (next === -1) {
      nodes.push(template.slice(i));
      break;
    }
    nodes.push(template.slice(i, next));
    i = next;
  }

  return nodes;
}

const UIPaginationBar = ({
  currentPage,
  pages,
  pageSize,
  pageSizeOptions,
  rangeText,
  rangeTemplate,
  rangeFrom,
  rangeTo,
  rangeTotal,
  rowsPerPageLabel,
  onPageChange,
  onPageSizeChange,
  className,
  PaginationComponent,
}) => {
  const preset = pageSizeOptions ?? UI_TABLE_DEFAULT_ROWS_PER_PAGE_OPTIONS;
  const selectOptions = withSelectableCurrentPageSize([...preset], pageSize);
  const Paginator = PaginationComponent || Pagination;

  const hasTemplate =
    rangeTemplate != null &&
    String(rangeTemplate).length > 0 &&
    rangeFrom != null &&
    rangeTo != null &&
    rangeTotal != null;

  const rangeContent = hasTemplate
    ? renderRangeTemplate(
        rangeTemplate,
        rangeFrom,
        rangeTo,
        rangeTotal,
        "ui-pagination-bar__range-from-to",
      )
    : rangeText;

  return (
    <div className={clx("ui-pagination-bar", className)}>
      <span className="ui-pagination-bar__range ui-pagination-bar__range-text">
        {rangeContent}
      </span>
      <div className="ui-pagination-bar__pages">
        <Paginator
          count={Math.max(pages, 1)}
          page={currentPage}
          handleChange={(_e, next) => onPageChange(next)}
          defaultPage={1}
        />
      </div>
      <div className="ui-pagination-bar__size ui-pagination-bar__size-inner">
        <span className="ui-pagination-bar__rows-label text-nowrap">
          {rowsPerPageLabel}
        </span>
        <FormControl size="small">
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            variant="outlined"
            IconComponent={ChevronDown}
            className="ui-pagination-bar__size-inner-select"
          >
            {selectOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

UIPaginationBar.propTypes = {
  currentPage: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  /** Use with `rangeTemplate` + `rangeFrom` / `rangeTo` / `rangeTotal`, or alone as a full string. */
  rangeText: PropTypes.string,
  /** i18n string with `{from}`, `{to}`, `{total}` — from–to segment uses brand gray 1000. */
  rangeTemplate: PropTypes.string,
  rangeFrom: PropTypes.number,
  rangeTo: PropTypes.number,
  rangeTotal: PropTypes.number,
  rowsPerPageLabel: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  PaginationComponent: PropTypes.elementType,
};

UIPaginationBar.defaultProps = {
  pageSizeOptions: undefined,
  rangeText: undefined,
  rangeTemplate: undefined,
  rangeFrom: undefined,
  rangeTo: undefined,
  rangeTotal: undefined,
  className: undefined,
  PaginationComponent: undefined,
};

export default UIPaginationBar;
